import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Pagination } from 'antd';
import { faUser } from '@fortawesome/pro-regular-svg-icons';
import sortBy from 'lodash-es/sortBy';
import { css } from 'styled-components';

import { trackEvent } from 'jf/analytics/Analytics';
import { SlackUser, TargetRespondent } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExEmpty } from 'jf/components/DevExEmpty';
import { DevExLoader } from 'jf/components/DevExLoader';
import { DevExSearch } from 'jf/components/DevExSearch';
import { DevExText } from 'jf/components/DevExText';

import { EmptySlackRosterDisplay } from './EmptySlackRosterDisplay';
import { SlackEntityDisplay } from './SlackEntityDisplay';
import { SlackUserWarning } from './SlackUserWarning';

type SlackTargetListProps = {
  users: SlackUser[];
  unlinkedUsers?: TargetRespondent[]; // list of unlinked users
  onLink?: () => void; // on link of unlinked slack user
  onUpdate: (users: SlackUser[]) => void;
  loading?: boolean;
  emptyText?: string | React.ReactElement; // text to display when list is empty
  emptyStateCTA?: string | React.ReactElement; // empty state secondary text button or text
  extra?: React.ReactElement[]; // additional header buttons
  usersLabel?: string | React.ReactElement; // label for user name, repondents or slack user etc
  trackEventKey: string; // key for tracking events
  loadingText?: string; // loading message
  saving?: boolean; // Saving or removing slack targets
};

const USERS_PER_PAGE = 10;

const styles = css`
  width: 100%;
  .slack-target-list__loading,
  .slack-target-list__empty {
    padding: ${({ theme }) => theme.variable.spacing.md};
    border: 1px solid ${({ theme }) => theme.color.border.secondary};
    border-radius: 5px;
    .empty__text-content,
    .loading__text-content {
      width: 80%;
      text-align: center;
      font-size:;
    }
  }
`;
export const SlackTargetList: React.FC<SlackTargetListProps> = ({
  users,
  onUpdate,
  unlinkedUsers,
  onLink,
  loading,
  emptyText,
  emptyStateCTA,
  extra,
  usersLabel,
  trackEventKey,
  loadingText,
  saving,
}) => {
  const theme = useDevExTheme();

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const sortedSlackUsers = useMemo(
    () => sortBy(users, (target) => target && target.userName.toLowerCase()),
    [users]
  );
  const sortedUnlinkedUsers = useMemo(
    () =>
      unlinkedUsers
        ? sortBy(unlinkedUsers, (target) =>
            target && target.name ? target.name.toLowerCase() : target.email.toLowerCase()
          )
        : [],
    [users]
  );
  const filteredUsers: {
    slackUsers: SlackUser[];
    unlinked: TargetRespondent[];
  } = useMemo(() => {
    const slackUsers = sortedSlackUsers.filter(
      (studySlackTarget) =>
        studySlackTarget &&
        ((studySlackTarget.userName &&
          studySlackTarget.userName.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (studySlackTarget.realName &&
            studySlackTarget.realName.toLowerCase().includes(searchQuery.toLowerCase())))
    );
    const unlinked = sortedUnlinkedUsers.filter(
      (unlinkedTarget) =>
        unlinkedTarget &&
        ((unlinkedTarget.name &&
          unlinkedTarget.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          unlinkedTarget.email.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    return {
      slackUsers,
      unlinked,
    };
  }, [sortedSlackUsers, searchQuery]);

  const filteredUsersByPage: SlackUser[] = useMemo(() => {
    const startIndex = (page - 1) * USERS_PER_PAGE;
    const endIndex = page * USERS_PER_PAGE;
    // TODO: We want unlinked and linked users to occupy the same list.
    // This means combining slack users and unlinked respondents into pagination code
    // if we eitehr return seperate filters here based on page, or concatenate
    // the lists and handle the type checking in the render function
    return filteredUsers.slackUsers.slice(startIndex, endIndex);
  }, [page, filteredUsers]);

  useEffect(() => {
    if ((page + 1) * USERS_PER_PAGE < filteredUsers.slackUsers.length) {
      setPage(1);
    }
  }, [filteredUsers]);

  const waitingForSync = false;

  const [editUsersActive, setEditUsersActive] = useState(false);

  const showUnlinked = editUsersActive && unlinkedUsers && !!unlinkedUsers.length && onLink;
  return (
    <Flex
      // prevent further interactions while the slack list is saving.
      // this will prevent conflicting adds or removes that are not honored.
      style={saving ? { pointerEvents: 'none', opacity: 0.4 } : {}}
      css={styles}
      vertical
      gap={theme.variable.spacing.sm}
    >
      {!!sortedUnlinkedUsers.length && !!users.length && (
        <SlackUserWarning
          users={sortedUnlinkedUsers}
          enabled={editUsersActive}
          onClick={() => setEditUsersActive(!editUsersActive)}
        />
      )}
      {loading ? (
        <Flex
          className="slack-target-list__loading"
          vertical
          align="center"
          justify="center"
          gap={theme.variable.spacing.lg}
        >
          <DevExLoader />
          <DevExText fontSize="lg">{loadingText ? loadingText : 'Searching Slack...'}</DevExText>
          {waitingForSync && ( // backend says that users aren't ready
            <DevExText fontSize="md">
              This is taking a bit longer than usual; please allow a couple more minutes. We
              appreciate your patience!
            </DevExText>
          )}
        </Flex>
      ) : (
        <>
          <Flex align="center" justify="space-between">
            <>
              {usersLabel || (
                <div style={{ color: theme.color.text.secondary }}>
                  {users.length || 0} Slack user
                  {users.length !== 1 ? 's' : ''} found
                </div>
              )}
            </>
            {saving && (
              <Flex align="center">
                <DevExLoader size="small" />
                <DevExText style={{ verticalAlign: 'middle' }} type="secondary" fontSize="sm">
                  Saving
                </DevExText>
              </Flex>
            )}
            <Flex gap={theme.variable.spacing.sm} align="center" justify="space-between">
              {!!users.length && (
                <DevExButton
                  size="default"
                  type="text"
                  onClick={() => {
                    trackEvent(`survey-editor:${trackEventKey}:remove-all`);
                    onUpdate([]);
                  }}
                  style={{ marginLeft: `-${theme.variable.spacing.sm}` }}
                >
                  Remove all
                </DevExButton>
              )}
              <DevExSearch
                size="small"
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                collapsible
                disabled={users.length === 0}
                outline
              />
              {extra && extra.map((el, i) => <React.Fragment key={i}>{el}</React.Fragment>)}
            </Flex>
          </Flex>
          {showUnlinked || users.length ? (
            <>
              {/* Users that we found without slack, and we've clicked the "link users" */}
              {showUnlinked &&
                filteredUsers.unlinked.map((user, i) => (
                  <Flex
                    key={user.publicReferenceId}
                    gap={theme.variable.spacing.xxl}
                    align="center"
                    justify="space-between"
                    style={{
                      padding: theme.variable.spacing.sm,
                      border: `1px solid`,
                      borderRadius: theme.variable.borderRadius,
                      borderColor: theme.color.border.primary,
                    }}
                  >
                    <EmptySlackRosterDisplay
                      user={user}
                      rosterUsers={filteredUsers.unlinked}
                      onAdd={() => {
                        setSearchQuery('');
                        onLink();
                      }}
                    />
                  </Flex>
                ))}
              {filteredUsersByPage.map((target, i) => (
                <SlackEntityDisplay
                  key={target.publicReferenceId}
                  slackUser={target}
                  onDelete={() => {
                    const _users = [...users];
                    _users.splice(
                      users.findIndex((u) => u.email === target.email),
                      1
                    );

                    trackEvent(`survey-editor:${trackEventKey}:remove`, {
                      slackUserId: target.slackUserId || null,
                    });
                    onUpdate(_users);
                  }}
                />
              ))}
              <Pagination
                current={page}
                hideOnSinglePage
                showSizeChanger={false}
                total={filteredUsers.slackUsers.length}
                pageSize={USERS_PER_PAGE}
                onChange={(page) => {
                  trackEvent(`study-editor:${trackEventKey}:pagination`, {
                    page,
                  });
                  setPage(page);
                }}
                style={{ alignSelf: 'center' }}
              />
            </>
          ) : (
            <Flex
              className="slack-target-list__loading"
              align="center"
              gap={theme.variable.spacing.lg}
              vertical
            >
              <DevExEmpty icon={faUser} iconSize={34} />
              <Flex
                className="empty__text-content"
                vertical
                align="center"
                gap={theme.variable.spacing.lg}
              >
                <DevExText fontSize="md" type="secondary" lined>
                  {emptyText ? emptyText : 'No Users'}
                </DevExText>
                {!!emptyStateCTA && (
                  <DevExText fontSize="md" type="secondary" lined>
                    {emptyStateCTA}
                  </DevExText>
                )}
              </Flex>
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};
